import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, createBlock as _createBlock, resolveDynamicComponent as _resolveDynamicComponent, toDisplayString as _toDisplayString, resolveDirective as _resolveDirective, withDirectives as _withDirectives, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-0d41c52e"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["tabindex"]
const _hoisted_2 = ["tabindex"]
const _hoisted_3 = ["tabindex"]
const _hoisted_4 = ["id", "src", "alt"]
const _hoisted_5 = { class: "flex gap-3" }
const _hoisted_6 = { class: "flex-grow py-1 sm:py-0" }
const _hoisted_7 = ["tabindex"]
const _hoisted_8 = ["id", "src", "alt"]
const _hoisted_9 = ["innerHTML"]
const _hoisted_10 = {
  key: 6,
  class: "mt-4 text-right mr-5"
}
const _hoisted_11 = ["tabindex"]
const _hoisted_12 = {
  key: 7,
  class: "mt-4"
}
const _hoisted_13 = ["tabindex"]
const _hoisted_14 = ["tabindex"]
const _hoisted_15 = ["tabindex"]

import {
  computed,
  reactive,
  onMounted,
  onUpdated,
  ref,
  nextTick,
  StyleValue,
} from 'vue'
import CloseIcon from '@/assets/svg/CloseIcon.vue'
import PlayIcon from '@/assets/svg/PlayIcon.vue'
import useLanguage from '@/composables/useLanguage'
import remedyData from '@/assets/data/remedy-data.js'
import type { Ref } from 'vue'
import useImageLoad from '@/composables/useImageLoad'
import { PopoverData, PopoverActionType } from './types'
import { useEventBus } from '@vueuse/core'
import { useSettingsStore } from '@/store/settings'
import useTabIndex from '@/composables/mjp/useTabIndex'
import PopOverVue from '@/components/general/PopOver.vue'
import AudioPlayer from '@/components/general/AudioPlayer.vue'
import CollectStormwaterIcon from '@/assets/svg/CollectStormwaterIcon.vue'
import ImprovePublicSpaceIcon from '@/assets/svg/ImprovePublicSpaceIcon.vue'
import ImproveTransitIcon from '@/assets/svg/ImproveTransitIcon.vue'
import PlantVegetationIcon from '@/assets/svg/PlantVegetationIcon.vue'
import RestoreWaterBodyIcon from '@/assets/svg/RestoreWaterBodyIcon.vue'
import AudioIcon from '@/assets/svg/AudioIcon.vue'
import VideoIcon from '@/assets/svg/VideoIcon.vue'
import InfoIcon from '@/assets/svg/InfoIcon.vue'

interface Props {
  index: number
  source: HTMLElement
  data: PopoverData
  text: object
  isVisible: boolean
  offsetX: number
  offsetY: number
  dir?: string
  baseTabIndex?: number
}

interface PopOverState {
  hotspotRect: DOMRect | null
  proxyDirection: string
  proxyAlignment: string
  isExpanded: boolean
  transcriptIsOpen: boolean
}

export default /*#__PURE__*/_defineComponent({
  __name: 'PopOver',
  props: {
    index: null,
    source: null,
    data: null,
    text: null,
    isVisible: { type: Boolean },
    offsetX: null,
    offsetY: null,
    dir: null,
    baseTabIndex: null
  },
  setup(__props: any, { expose }) {

const props = __props as {
  index: number
  source: HTMLElement
  data: PopoverData
  text: object
  isVisible: boolean
  offsetX: number
  offsetY: number
  dir?: string
  baseTabIndex?: number
}


const interfaceText = useLanguage('interface')
const settingsStore = useSettingsStore()
const state: PopOverState = reactive({
  hotspotRect: null,
  proxyDirection: props.data.direction, // we use proxies in case we need to make programmatic adjustments
  proxyAlignment: props.data.alignment,
  isExpanded: false,
  transcriptIsOpen: false,
})
let lastVisible = false
const margin = { x: 58, y: 58 }
const secondaryMargin = 27
const tertiaryMargin = 35
const element: Ref<HTMLElement | null> = ref(null)
const audioPlayer: Ref<typeof AudioPlayer | null> = ref(null)
const popoverActionTriggeredBus = useEventBus('popover-action-triggered')
const popoversNeedUpdateBus = useEventBus('popovers-need-update')
const popoverOpenedBus = useEventBus('popover-opened')
const popoverClosedBus = useEventBus('popover-closed')
const layoutUpdatedBus = useEventBus('layout-updated')
const hotspotClickedBus = useEventBus('hotspot-clicked')
const knowledgeBaseBus = useEventBus('open-article')
const transcriptOpenedBus = useEventBus('transcript-opened')
const transcriptClosedBus = useEventBus('transcript-closed')
const windowSize = {
  x: window.innerWidth,
  y: window.innerHeight,
}

const remedyCategoryIcons = {
  collectStormwater: CollectStormwaterIcon,
  plantVegetation: PlantVegetationIcon,
  improveTransit: ImproveTransitIcon,
  improvePublicSpace: ImprovePublicSpaceIcon,
  restoreWaterBody: RestoreWaterBodyIcon,
  audio: AudioIcon,
  video: VideoIcon,
  info: InfoIcon,
}

// TODO: add hover and other states as needed, possibly make a composable
const bgColors = {
  sunset: 'bg-sunset',
  poppy: 'bg-poppy',
  mallow: 'bg-mallow',
  sprout: 'bg-sprout',
  aquafer: 'bg-aquafer',
  lupine: 'bg-lupine',
  manzanita: 'bg-manzanita',
  phacelia: 'bg-phacelia',
}

const borderColors = {
  sunsetLeft: 'border-l-sunset',
  poppyLeft: 'border-l-poppy',
  mallowLeft: 'border-l-mallow',
  sproutLeft: 'border-l-sprout',
  aquaferLeft: 'border-l-aquafer',
  lupineLeft: 'border-l-lupine',
  manzanitaLeft: 'border-l-manzanita',
  phaceliaLeft: 'border-l-phacelia',
  sunsetRight: 'border-r-sunset',
  poppyRight: 'border-r-poppy',
  mallowRight: 'border-r-mallow',
  sproutRight: 'border-r-sprout',
  aquaferRight: 'border-r-aquafer',
  lupineRight: 'border-r-lupine',
  manzanitaRight: 'border-r-manzanita',
  phaceliaRight: 'border-r-phacelia',
  sunsetTop: 'border-t-sunset',
  poppyTop: 'border-t-poppy',
  mallowTop: 'border-t-mallow',
  sproutTop: 'border-t-sprout',
  aquaferTop: 'border-t-aquafer',
  lupineTop: 'border-t-lupine',
  manzanitaTop: 'border-t-manzanita',
  phaceliaTop: 'border-t-phacelia',
  sunsetBottom: 'border-b-sunset',
  poppyBottom: 'border-b-poppy',
  mallowBottom: 'border-b-mallow',
  sproutBottom: 'border-b-sprout',
  aquaferBottom: 'border-b-aquafer',
  lupineBottom: 'border-b-lupine',
  manzanitaBottom: 'border-b-manzanita',
  phaceliaBottom: 'border-b-phacelia',
}

const handleResize = () => {
  updateWindowSize()
  if (props.source) {
    state.hotspotRect = props.source.getBoundingClientRect()
  }
}

const updateWindowSize = () => {
  windowSize.x = window.innerWidth
  windowSize.y = window.innerHeight
}

const alignmentType = computed(() => {
  return state.proxyDirection == 'top' || state.proxyDirection == 'bottom'
    ? 'vertical'
    : 'horizontal'
})

const positionStyles = computed(() => {
  handleResize()
  let styles: StyleValue = {}
  let proxyOffsetX = props.offsetX ? props.offsetX : 0
  let proxyOffsetY = props.offsetY ? props.offsetY : 0
  let boundingRect
  if (state.hotspotRect && element.value && windowSize.x >= 768) {
    if (state.hotspotRect.width > 0 && state.hotspotRect.height > 0) {
      const pos = {
        x: state.hotspotRect.x + state.hotspotRect.width * 0.5,
        y: state.hotspotRect.y + state.hotspotRect.height * 0.5,
      }
      switch (state.proxyDirection) {
        case 'left':
          styles.right = windowSize.x - pos.x + margin.x + proxyOffsetX + 'px'
          break
        case 'right':
          styles.left = pos.x + margin.x - proxyOffsetX + 'px'
          break
        case 'top':
          styles.bottom = windowSize.y - pos.y + margin.y + proxyOffsetY + 'px'
          break
        case 'bottom':
          styles.top = pos.y + margin.y - proxyOffsetY + 'px'
          break
      }
      switch (state.proxyAlignment) {
        case 'left':
          styles.right =
            windowSize.x - pos.x - secondaryMargin - proxyOffsetX + 'px'
          break
        case 'center':
          boundingRect = element.value.getBoundingClientRect()
          if (alignmentType.value == 'horizontal') {
            styles.top = pos.y - boundingRect.height * 0.5 + proxyOffsetY + 'px'
          } else {
            styles.left = pos.x - boundingRect.width * 0.5 + proxyOffsetX + 'px'
          }
          break
        case 'right':
          styles.left = pos.x - tertiaryMargin - proxyOffsetX + 'px'
          break
        case 'top':
          styles.bottom =
            windowSize.y - pos.y - secondaryMargin + proxyOffsetY + 'px'
          break
        case 'bottom':
          styles.top = pos.y - secondaryMargin - proxyOffsetY + 'px'
          break
      }
    }
  }
  return styles
})

const pointerStyles = computed(() => {
  let styles: StyleValue = {}
  if (state.hotspotRect && element.value) {
    if (state.hotspotRect.width > 0 && state.hotspotRect.height > 0) {
      switch (state.proxyDirection) {
        case 'left':
          styles.left = '100%'
          break
        case 'right':
          styles.left = '-16px'
          break
        case 'top':
          break
        case 'bottom':
          styles.top = '-16px'
          break
      }
      switch (state.proxyAlignment) {
        case 'left':
          styles.left = 'calc(100% - 36px)'
          break
        case 'center':
          if (alignmentType.value == 'horizontal') {
            styles.top = 'calc(50% - 9px)'
          } else {
            styles.left = 'calc(50% - 9px)'
          }
          break
        case 'right':
          styles.left = '16px'
          break
        case 'top':
          styles.top = 'calc(100% - 36px)'
          break
        case 'bottom':
          styles.top = '16px'
          break
      }
    }
  }
  return styles
})

const containerStyles = computed(() => {
  let styles = bgColors[remedyCategoryColor.value]
  if (state.isExpanded) {
    styles += ' py-4 sm:py-2 sm:pb-30px pr-4 pl-4 sm:pr-5 sm:pl-10'
  } else if (props.data.action?.type == 'playAudio') {
    styles += ' p-4'
  } else if (props.data.action?.type != 'preview') {
    styles += ' p-4'
  } else {
    styles += ' px-4 py-1'
    if (props.isVisible) {
      styles += ' pointer-events-auto cursor-pointer'
    }
  }
  if (windowSize.x >= 768) {
    if (state.isExpanded) {
      styles += ' w-[544px]'
    } else if (props.data.action?.type == 'playAudio') {
      styles += ' w-[300px]'
    } else if (props.data.action?.type != 'preview') {
      styles += ' w-[215px]'
    }
  }
  return styles
})

const containerTextStyles = computed(() => {
  let styles = ''
  if (state.isExpanded) {
    styles = 'sm:mt-30px'
  } else if (props.data.action?.type == 'preview') {
    styles = 'text-center'
  }
  return styles
})

const remedyCategoryColor = computed(() => {
  return props.data.remedyCategory
    ? remedyData.remedyCategories[props.data.remedyCategory]?.color
    : 'poppy'
})

popoversNeedUpdateBus.on(() => {
  state.proxyDirection = props.data.direction
  state.proxyAlignment = props.data.alignment
  updateLayout()
})

layoutUpdatedBus.on(() => {
  updateLayout()
})

const updateLayout = () => {
  handleResize()
}

expose({ updateLayout })

const mediaId = computed(() => {
  let a = props.data.thumbnail?.split('/')
  let b = a[a.length - 1].split('.')
  return b[0]
})

const handlePopoverActionClicked = (evt) => {
  if (props.data.action.type == PopoverActionType.LearnMore) {
    state.isExpanded = true
    const elements = element.value?.getElementsByClassName('popover-content')
    if (elements) {
      const element = elements[0] as HTMLElement
      element.focus()
    }
    nextTick(() => {
      updateLayout()
    })
  } else {
    popoverActionTriggeredBus.emit({ event: evt, action: props.data.action })
  }
  evt.stopPropagation()
}

const handleSecondaryPopoverActionClicked = (evt) => {
  popoverActionTriggeredBus.emit({
    event: evt,
    action: {
      type: PopoverActionType.GoToSection,
      destination: props.data.action.destination,
    },
  })
}

const handleCloseButtonClicked = (evt) => {
  state.isExpanded = false
  if (audioPlayer.value) {
    audioPlayer.value.pause()
  }
  popoverClosedBus.emit()
}

const handlePopoverClicked = (evt) => {
  if (props.data.action?.type == 'preview') {
    hotspotClickedBus.emit(props.index)
    evt.stopPropagation()
  }
}

const handleTranscriptClicked = (evt) => {
  const target = evt.currentTarget as HTMLElement
  const rect = target.getBoundingClientRect()
  const info = {
    x: rect.x + rect.width * 0.5,
    y: rect.y + rect.height * 0.5,
    transcript: props.data.transcript,
  }
  state.transcriptIsOpen = true
  transcriptOpenedBus.emit(info)
}

transcriptClosedBus.on(() => {
  state.transcriptIsOpen = false
  if (audioPlayer.value) {
    audioPlayer.value.pause()
  }
})

onUpdated(() => {
  if (!props.isVisible) {
    // wait until we fade out before collapsing
    setTimeout(() => {
      state.isExpanded = false
    }, 500)
    // if the transcript isn't open, pause any audio
    if (!state.transcriptIsOpen) {
      if (audioPlayer.value) {
        audioPlayer.value.pause()
      }
    }
  } else {
    if (!lastVisible) {
      let link = element.value?.querySelector(
        '.compact-action'
      ) as HTMLAnchorElement
      if (link) link.focus()
      popoverOpenedBus.emit()
    }
  }
  lastVisible = props.isVisible
})

onMounted(() => {
  setTimeout(() => {
    updateLayout()
  }, 1000) // a kludge, since getBoundingClientRect is returning zero initially
})

const baseTabIndex = computed(() => {
  return props.baseTabIndex ? props.baseTabIndex : 0
})

const openArticle = (evt) => {
  knowledgeBaseBus.emit(
    remedyData.remedyCategories[props.data.remedyCategory].articleId
  )
}

return (_ctx: any,_cache: any) => {
  const _directive_resize = _resolveDirective("resize")!

  return _withDirectives((_openBlock(), _createElementBlock("div", {
    ref_key: "element",
    ref: element,
    class: _normalizeClass(["popover fixed bottom-20 left-6 right-6 sm:bottom-auto sm:left-auto sm:right-auto", {
      invisible: !props.isVisible,
      'opacity-0': !props.isVisible,
      'opacity-100': props.isVisible,
      'pointer-events-none': !props.isVisible,
      'pointer-events-auto': props.isVisible,
      'text-slateGray': !_unref(settingsStore).highContrast,
      'text-slateTone':
        _unref(settingsStore).highContrast && !props.data.remedyCategory,
      'text-charcoalGray':
        _unref(settingsStore).highContrast && props.data.remedyCategory,
    }]),
    style: _normalizeStyle(_unref(positionStyles))
  }, [
    (state.proxyDirection == 'left')
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass(["absolute hidden sm:block border-solid border-l-[16px] border-t-[9px] border-t-transparent border-b-[9px] border-b-transparent", borderColors[_unref(remedyCategoryColor) + 'Left']]),
          style: _normalizeStyle(_unref(pointerStyles))
        }, null, 6))
      : _createCommentVNode("", true),
    (state.proxyDirection == 'bottom')
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          class: _normalizeClass(["absolute hidden sm:block border-solid border-b-[16px] border-l-[9px] border-l-transparent border-r-[9px] border-r-transparent", borderColors[_unref(remedyCategoryColor) + 'Bottom']]),
          style: _normalizeStyle(_unref(pointerStyles))
        }, null, 6))
      : _createCommentVNode("", true),
    (state.isExpanded)
      ? (_openBlock(), _createElementBlock("button", {
          key: 2,
          class: "absolute hidden sm:block top-5 right-5",
          tabindex: 
        props.isVisible
          ? _unref(useTabIndex)(PopOverVue, _unref(baseTabIndex), 'closeBtn', -1)
          : -1
      ,
          onClick: handleCloseButtonClicked
        }, [
          _createVNode(CloseIcon, { class: "cursor-pointer mt-1" })
        ], 8, _hoisted_1))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      class: _normalizeClass(["rounded-lg text-sm font-sans tracking-wide flex flex-col max-h-[360px]", _unref(containerStyles)]),
      onClick: handlePopoverClicked
    }, [
      _createElementVNode("div", {
        class: _normalizeClass(["popover-content overflow-y-auto flex-shrink sm:text-left", _unref(containerTextStyles)]),
        tabindex: 
          state.isExpanded
            ? _unref(useTabIndex)(PopOverVue, _unref(baseTabIndex), 'expandedText', -1)
            : -1
        
      }, [
        (props.data.thumbnail && !state.isExpanded)
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              class: _normalizeClass(["relative mb-2 flex items-center justify-center", {
            'cursor-pointer':
              props.data.action && props.data.action?.type == 'playVideo',
          }]),
              onClick: handlePopoverActionClicked,
              tabindex: 
            props.isVisible && props.data.action?.type == 'playVideo'
              ? _unref(useTabIndex)(PopOverVue, _unref(baseTabIndex), 'video', -1)
              : -1
          
            }, [
              _createElementVNode("img", {
                id: _unref(mediaId),
                class: "rounded-lg w-full max-h-[200px] sm:w-auto sm:max-h-max object-cover",
                "data-test": "thumbnail-image",
                onLoad: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(useImageLoad) && _unref(useImageLoad)(...args))),
                src: 
              props.data.thumbnail
                ? `/images/${props.dir}/${props.data.thumbnail}`
                : undefined
            ,
                alt: props.text[props.data.thumbnailAlt]
              }, null, 40, _hoisted_4),
              (props.data.action?.type == 'playVideo')
                ? (_openBlock(), _createBlock(PlayIcon, {
                    key: 0,
                    class: "absolute mt-2 drop-shadow-playButton"
                  }))
                : _createCommentVNode("", true)
            ], 10, _hoisted_3))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            (props.data.action?.type == 'playAudio')
              ? (_openBlock(), _createBlock(AudioPlayer, {
                  key: 0,
                  ref_key: "audioPlayer",
                  ref: audioPlayer,
                  class: "mb-2",
                  tabindex: 
                props.isVisible && props.data.action?.type == 'playAudio'
                  ? _unref(useTabIndex)(PopOverVue, _unref(baseTabIndex), 'audio', -1)
                  : -1
              ,
                  src: `/audio/${props.dir}/${props.data.action.content}`
                }, null, 8, ["tabindex", "src"]))
              : _createCommentVNode("", true),
            (props.data.action?.type == 'preview' && !state.isExpanded)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 1,
                  class: "sm:hidden",
                  onClick: openArticle
                }, [
                  (_openBlock(), _createBlock(_resolveDynamicComponent(remedyCategoryIcons[props.data.remedyCategory])))
                ]))
              : _createCommentVNode("", true),
            (!state.isExpanded)
              ? (_openBlock(), _createElementBlock("p", {
                  key: 2,
                  class: _normalizeClass(["popover-text", {
                'mb-4':
                  (props.data.action &&
                    props.data.action?.type == 'learnMore') ||
                  props.data.transcript,
                'text-center': props.data.action?.type == 'preview',
                'font-medium': props.data.action?.type == 'preview',
              }]),
                  "data-test": "body-copy"
                }, _toDisplayString(props.text[props.data.text]), 3))
              : _createCommentVNode("", true),
            (props.data.transcript)
              ? (_openBlock(), _createElementBlock("a", {
                  key: 3,
                  href: "#",
                  onClick: handleTranscriptClicked,
                  tabindex: 
                props.isVisible
                  ? _unref(useTabIndex)(PopOverVue, _unref(baseTabIndex), 'transcript', -1)
                  : -1
              
                }, _toDisplayString(_unref(interfaceText).openTranscript), 9, _hoisted_7))
              : _createCommentVNode("", true),
            (props.data.thumbnail && state.isExpanded)
              ? (_openBlock(), _createElementBlock("img", {
                  key: 4,
                  id: _unref(mediaId),
                  class: "float-left rounded-lg max-w-[200px] mr-8 mb-5",
                  "data-test": "thumbnail-image",
                  onLoad: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (_unref(useImageLoad) && _unref(useImageLoad)(...args))),
                  src: 
                props.data.thumbnail
                  ? `/images/${props.dir}/${props.data.thumbnail}`
                  : undefined
              ,
                  alt: props.text[props.data.thumbnailAlt]
                }, null, 40, _hoisted_8))
              : _createCommentVNode("", true),
            (state.isExpanded && props.data.action.content)
              ? (_openBlock(), _createElementBlock("p", {
                  key: 5,
                  class: "popover-text sm:mr-5",
                  "data-test": "body-expanded-copy",
                  innerHTML: props.text[props.data.action.content]
                }, null, 8, _hoisted_9))
              : _createCommentVNode("", true),
            (
                state.isExpanded &&
                props.data.action?.destination &&
                props.data.action.destinationText
              )
              ? (_openBlock(), _createElementBlock("p", _hoisted_10, [
                  _createElementVNode("a", {
                    class: "underline underline-offset-2 font-sans font-medium",
                    href: "#",
                    tabindex: 
                  props.isVisible && state.isExpanded
                    ? _unref(useTabIndex)(PopOverVue, _unref(baseTabIndex), 'actionLink', -1)
                    : -1
                ,
                    onClick: handleSecondaryPopoverActionClicked
                  }, _toDisplayString(_unref(interfaceText)[props.data.action.destinationText]), 9, _hoisted_11)
                ]))
              : _createCommentVNode("", true),
            (
                !state.isExpanded &&
                props.data.action &&
                props.data.action?.type != 'preview' &&
                props.data.action.text
              )
              ? (_openBlock(), _createElementBlock("p", _hoisted_12, [
                  _createElementVNode("a", {
                    class: "compact-action underline underline-offset-2 font-sans font-medium",
                    href: "#",
                    tabindex: 
                  props.isVisible && !state.isExpanded
                    ? _unref(useTabIndex)(PopOverVue, _unref(baseTabIndex), 'actionLink', -1)
                    : -1
                ,
                    onClick: handlePopoverActionClicked
                  }, _toDisplayString(_unref(interfaceText)[props.data.action.text]), 9, _hoisted_13)
                ]))
              : _createCommentVNode("", true)
          ]),
          (props.data.action?.type != 'preview' && !state.isExpanded)
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                class: "flex-shrink h-[14px] mt-1",
                tabindex: 
              props.isVisible
                ? _unref(useTabIndex)(PopOverVue, _unref(baseTabIndex), 'closeBtn', -1)
                : -1
            ,
                onClick: handleCloseButtonClicked
              }, [
                _createVNode(CloseIcon, { class: "hidden sm:block cursor-pointer" })
              ], 8, _hoisted_14))
            : _createCommentVNode("", true)
        ])
      ], 10, _hoisted_2),
      (props.data.action?.type != 'preview')
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "relative right-0 sm:hidden mt-4 text-right",
            tabindex: 
          props.isVisible
            ? _unref(useTabIndex)(PopOverVue, _unref(baseTabIndex), 'closeBtn', -1)
            : -1
        ,
            onClick: handleCloseButtonClicked
          }, [
            _createVNode(CloseIcon, { class: "cursor-pointer inline-block mt-1" })
          ], 8, _hoisted_15))
        : _createCommentVNode("", true)
    ], 2),
    (state.proxyDirection == 'right')
      ? (_openBlock(), _createElementBlock("div", {
          key: 3,
          class: _normalizeClass(["absolute hidden sm:block border-solid border-r-[16px] border-t-[9px] border-t-transparent border-b-[9px] border-b-transparent", borderColors[_unref(remedyCategoryColor) + 'Right']]),
          style: _normalizeStyle(_unref(pointerStyles))
        }, null, 6))
      : _createCommentVNode("", true),
    (state.proxyDirection == 'top')
      ? (_openBlock(), _createElementBlock("div", {
          key: 4,
          class: _normalizeClass(["absolute hidden sm:block border-solid border-t-[16px] border-l-[9px] border-l-transparent border-r-[9px] border-r-transparent", borderColors[_unref(remedyCategoryColor) + 'Top']]),
          style: _normalizeStyle(_unref(pointerStyles))
        }, null, 6))
      : _createCommentVNode("", true)
  ], 6)), [
    [_directive_resize, handleResize]
  ])
}
}

})